import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, TestTube, Workflow } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { TaxAccountPipeline } from '@/components/TaxAccountPipeline'

export function TestlabPipelinePage() {
  const { t } = useTranslation('common')

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/testlab">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka till Testlab
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <Workflow className="w-6 h-6" />
          <h1 className="text-2xl font-bold">Skattekontoanalys - Hybridpipeline</h1>
          <Badge variant="secondary" className="text-xs">
            TESTLAB
          </Badge>
        </div>
      </div>

      <Alert>
        <TestTube className="h-4 w-4" />
        <AlertDescription>
          <strong>Experimentell pipeline för skattekontoanalys</strong>
          <br />
          Detta är en testmiljö för det nya hybridflödet: Precheck → Deterministisk analys → Batchad AI-helanalys.
          <br />
          <strong>Ny funktion:</strong> AI analyserar nu ALLA findings (1000+) genom token-budgeterad batchning:
          Mini-modell poängsätter alla avvikelser, Premium-modell ger djupförklaringar för topp-100.
          Ingen hård begränsning längre - fullständig täckning med skalbar prestanda.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TaxAccountPipeline />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pipeline-steg</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <strong>1. Precheck</strong>
                <p className="text-muted-foreground">
                  Snabb parsing av header + 5k rader. AI-baserad fältmappning och normalisering.
                </p>
              </div>
              <div>
                <strong>2. Deterministisk analys</strong>
                <p className="text-muted-foreground">
                  Kör deterministiska regler på hela filen: försenade betalningar, avgifter, avvikelser.
                </p>
              </div>
              <div>
                <strong>3. Batchad AI-helanalys</strong>
                <p className="text-muted-foreground">
                  <strong>Pass 1:</strong> Mini-modell poängsätter ALLA findings i token-budgeterade batcher (80k tokens/batch).
                  <br />
                  <strong>Pass 2:</strong> Premium-modell skapar djupförklaringar för topp-100 findings.
                  <br />
                  Ingen hård begränsning - alla avvikelser får AI-bedömning.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Regler som testas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Försenad inbetalning</span>
                <Badge variant="outline">{'>'} 3 dagar</Badge>
              </div>
              <div className="flex justify-between">
                <span>Avgift utan skuld</span>
                <Badge variant="outline">±60 dagar</Badge>
              </div>
              <div className="flex justify-between">
                <span>Deklarationsavvikelse</span>
                <Badge variant="outline">{'>'} 15%</Badge>
              </div>
              <div className="flex justify-between">
                <span>Oregelbunden prelim.</span>
                <Badge variant="outline">{'>'} 30%</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AI-modeller</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <strong>GPT-4.1-mini (Batchad poängsättning)</strong>
                <p className="text-muted-foreground">
                  Poängsätter ALLA findings 0-100 i token-budgeterade batcher.
                  Parallell bearbetning med 4 batcher samtidigt.
                </p>
              </div>
              <div>
                <strong>GPT-5-premium (Djupförklaringar)</strong>
                <p className="text-muted-foreground">
                  Skapar detaljerade förklaringar för topp-100 findings.
                  Sekventiell bearbetning för högkvalitativ analys.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}